import { Autocomplete, Box, IconButton, Pagination, Stack, styled, Typography } from '@mui/material'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from '../../mui-theme'
import { GenericTableProps } from './GenericTable'

export const MINIFIED_COLUMN_HEADER_HEIGHT = 35
/** Breathing room between the column headers and the first row. */
export const COLUMN_HEADER_GAP = 8

export const StyledDataGridPro = styled(DataGridPro)<GenericTableProps>(({ theme, columns, ...props }) => ({
  border: 'none',
  width: '100%',
  minWidth: 0,
  color: getThemeColor(theme, MUIThemeColors.black),
  [`& .${gridClasses.row}`]: {
    border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
    boxSizing: 'border-box',
    maxWidth: 'calc(100% - 2px)',
    height: props.getRowHeight ?? '35px',
    minHeight: props.minRowHeight ?? '44px !important',
    borderRadius: '0px',
    overflowWrap: 'break-word',
    cursor: props.onRowClick ? 'pointer' : '',
    '& :focus': {
      outline: props.onRowClick ? `solid ${getThemeColor(theme, MUIThemeColors.primaryMain)} 1px` : 'none',
    },
    '&.even': {
      backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    },
    '&.odd': {
      backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
    },
    '&:hover': {
      ['& span.MuiCheckbox-root']: {
        display: 'inline-flex',
      },
    },
    '&.MuiDataGrid-row--dragging': {
      backgroundColor: getThemeColor(theme, MUIThemeColors.primaryLight),
      opacity: 0.8,
    },
    '&.MuiDataGrid-row--dropZone': {
      backgroundColor: getThemeColor(theme, MUIThemeColors.primaryLight),
      borderColor: getThemeColor(theme, MUIThemeColors.primaryMain),
      borderWidth: '2px',
    },
  },
  [`& .${gridClasses.columnHeaders}`]: {
    minHeight: props.isMinifiedColumnHeader ? `${MINIFIED_COLUMN_HEADER_HEIGHT}px !important` : undefined,
    color: getThemeColor(theme, MUIThemeColors.black),
    border: 'none',
    display: props.hideColumnHeaders ? 'none !important' : undefined,
    [`& .${gridClasses.columnHeaderTitle}`]: {
      fontSize: '14px',
      fontWeight: 400,
      color: props.headerColor ?? getThemeColor(theme, MUIThemeColors.black),
    },
  },
  [`& .${gridClasses['pinnedColumnHeaders--left']}`]: {
    minHeight: props.isMinifiedColumnHeader ? `${MINIFIED_COLUMN_HEADER_HEIGHT}px !important` : undefined,
    color: getThemeColor(theme, MUIThemeColors.black),
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    border: 'none',
    [`& .${gridClasses.columnHeaderTitle}`]: {
      fontSize: '14px',
      fontWeight: 400,
      color: props.headerColor ?? getThemeColor(theme, MUIThemeColors.black),
    },
    boxShadow: 'none',
  },
  [`& .${gridClasses['pinnedColumns--left']}`]: {
    boxShadow: 'none',
  },
  [`& .${gridClasses.columnHeadersInner}`]: {
    lineHeight: '35px',
  },
  [`& .${gridClasses['columnSeparator']}`]: {
    visibility: 'hidden',
  },
  [`& .${gridClasses['cell']}`]: {
    border: 'none',
    fontSize: '12px',
    fontWeight: '400',
    color: getThemeColor(theme, MUIThemeColors.black),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    '&:focus': {
      outline: props.isCellEditable ? `solid ${getThemeColor(theme, MUIThemeColors.primaryMain)} 1px` : 'none',
    },
    minHeight: props.minRowHeight ?? '44px !important',
    // Normalise text rendered through custom cell renderers (Typography variants such as
    // body1/subtitle2 carry their own larger font size) down to the compact table size.
    ['& .MuiTypography-root']: {
      fontSize: '12px',
    },
  },
  [`& .${gridClasses['footerContainer']}`]: {
    border: 'none',
    display: props.hideDefaultFooterPagination ? 'none' : undefined,
  },
  [`& .${gridClasses['virtualScroller']}`]: {
    // The grid sets this inline to the header height; add the gap on top so the first row
    // does not sit flush against the headers.
    marginTop: props.hideColumnHeaders
      ? '0 !important'
      : props.isMinifiedColumnHeader
      ? `${MINIFIED_COLUMN_HEADER_HEIGHT + COLUMN_HEADER_GAP}px !important`
      : undefined,
    overflowX: props.allowHorizontalScroll ? undefined : 'hidden !important',
  },
  [`& .${gridClasses['virtualScrollerContent']}`]: {
    width: '100% !important',
    minWidth: 'unset !important',
  },
  [`& .${gridClasses['actionsCell']}`]: {
    // commented out due to a MUI bug where it applies a dimensionless(dot like) border to empty set of action buttons
    // border: `2px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
    // borderRadius: '4px',
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    opacity: 1,
    '& .MuiSvgIcon-root': {
      color: getThemeColor(theme, MUIThemeColors.primaryMain),
    },
  },
  // added after removing the border from actionsCell above to preserve the appearnce as before
  [`& .${gridClasses['actionsCell']} > .MuiIconButton-root`]: {
    border: `2px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
    borderRadius: '4px',
  },
  [`& .${gridClasses.cellCheckbox} .MuiCheckbox-root`]: {
    padding: '12px',
  },
  ['& span.MuiCheckbox-root']: {
    display: !props.enableUXFeatures ? 'inline-flex' : 'none',
  },
  ['& span.MuiCheckbox-root.Mui-checked']: {
    display: 'inline-flex',
  },
}))

export const ColumnSelector = styled('div')(({ theme }) => ({
  position: 'inherit',
  top: '50%',
  left: '87%',
  height: '98%',
  width: '25%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  padding: '10px',
  outline: 'none',
}))

export const ColorAndTitle = styled(Box)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  fontWeight: 'bold',
}))

export const ColumnOption = styled(Stack)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  margin: '20px',
}))

export const OpenTitle = styled(Typography)({
  fontSize: '0.8rem',
})

export const CloseButton = styled(IconButton)({
  color: 'red',
})

export const ColumnOptionInput = styled(Autocomplete)({
  '& .MuiOutlinedInput-root': {
    padding: 0,
  },
})

export const PaginationComponent = styled(Pagination)(({ theme }) => ({
  '& .MuiPaginationItem-root': {
    border: 0,
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  },
  '& .selected': {
    backgroundColor: `${getThemeColor(theme, MUIThemeColors.primaryDark)} !important`,
    outline: `1px solid ${getThemeColor(theme, MUIThemeColors.primaryMain)}`,
    borderRadius: '8px',
  },
}))
